/**
 * Shared collapsible JSON renderer.
 *
 * Breaks an arbitrary JSON value into an expandable key/value tree:
 * scalars render inline (colour-coded by type), nested objects/arrays
 * become their own `<details>` collapsed by default with a field/item
 * count. Used by the /data browser's JSON columns AND the member /
 * trainer detail + trainer-today pages so none dumps a huge one-line
 * blob.
 *
 * Reuses the `tl-pl-*` CSS classes (defined in styles.css).
 */

export function JsonTree({ value }: { value: unknown }): JSX.Element {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="tl-pl-empty">[]</span>;
    return (
      <div className="tl-pl-tree">
        {value.map((v, i) => (
          <JsonEntry key={i} label={`[${i}]`} value={v} />
        ))}
      </div>
    );
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="tl-pl-empty">{"{}"}</span>;
    return (
      <div className="tl-pl-tree">
        {entries.map(([k, v]) => (
          <JsonEntry key={k} label={k} value={v} />
        ))}
      </div>
    );
  }
  return <span className={scalarClass(value)}>{renderScalar(value)}</span>;
}

/**
 * Top-level collapsible wrapper — renders a container value (object or
 * array) as a single `<details>` with a "{ } N fields" / "[ ] N items"
 * summary. Collapsed by default; pass `defaultOpen` to render it expanded
 * (top-level fields visible, nested containers still lazy). Scalars fall
 * through to an inline span. Used by /data cells so a JSON column shows a
 * one-line caret instead of a blob, and by the member page's Metadata row.
 */
export function CollapsibleJson({
  value,
  defaultOpen = false,
}: {
  value: unknown;
  defaultOpen?: boolean;
}): JSX.Element {
  const isArr = Array.isArray(value);
  const isObj = value !== null && typeof value === "object";
  if (!isArr && !isObj) {
    return <span className={scalarClass(value)}>{renderScalar(value)}</span>;
  }
  const count = isArr
    ? (value as ReadonlyArray<unknown>).length
    : Object.keys(value as Record<string, unknown>).length;
  if (count === 0) {
    return <span className="tl-pl-empty">{isArr ? "[]" : "{}"}</span>;
  }
  return (
    <details
      className="tl-pl-nested json-cell"
      open={defaultOpen}
      // Inside clickable /data rows, expanding JSON must not open the
      // row-edit modal — swallow the click at the cell boundary.
      onClick={(e) => e.stopPropagation()}
    >
      <summary className="tl-pl-nested-summary">
        <span className="tl-pl-meta">
          {isArr ? "[ ]" : "{ }"} {count} {isArr ? "items" : "fields"}
        </span>
      </summary>
      <div className="tl-pl-nested-body">
        <JsonTree value={value} />
      </div>
    </details>
  );
}

function JsonEntry({
  label,
  value,
}: {
  label: string;
  value: unknown;
}): JSX.Element {
  const isContainer =
    Array.isArray(value) || (value !== null && typeof value === "object");
  if (isContainer) {
    const count = Array.isArray(value)
      ? value.length
      : Object.keys(value as Record<string, unknown>).length;
    const kind = Array.isArray(value) ? "items" : "fields";
    return (
      <details className="tl-pl-nested">
        <summary className="tl-pl-nested-summary">
          <span className="tl-pl-key">{label}</span>
          <span className="tl-pl-meta">
            {Array.isArray(value) ? "[ ]" : "{ }"} {count} {kind}
          </span>
        </summary>
        <div className="tl-pl-nested-body">
          <JsonTree value={value} />
        </div>
      </details>
    );
  }
  return (
    <div className="tl-pl-kv">
      <span className="tl-pl-key">{label}</span>
      <span className={scalarClass(value)}>{renderScalar(value)}</span>
    </div>
  );
}

export function renderScalar(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  return JSON.stringify(value);
}

export function scalarClass(value: unknown): string {
  if (value === null) return "tl-pl-val tl-pl-null";
  if (typeof value === "number") return "tl-pl-val tl-pl-num";
  if (typeof value === "boolean") return "tl-pl-val tl-pl-bool";
  return "tl-pl-val tl-pl-str";
}
